/* eslint-disable react/require-default-props */
import React from 'react';
import { Box, BoxProps, ChakraProps } from '@chakra-ui/core';
import { FaSortDown, FaSortUp, FaSort } from 'react-icons/fa';
import { HeaderGroup, SortingRule } from 'react-table';
import colors from '../../../styles/colors';

type DataType = Record<string, unknown>;

interface SortIcons {
  ascending?: React.ReactNode;
  descending?: React.ReactNode;
  unsorted?: React.ReactNode;
}

interface TableHeadCellProps<T extends DataType> extends Omit<BoxProps, 'children'> {
  column: HeaderGroup<T>;
  children: React.ReactNode;
  sortIcons?: SortIcons;
  enableSortingIcons?: boolean;
  onSort?: (columnId: string, desc: boolean | undefined) => void;
  headerProps?: ChakraProps;
}

function TableHeadCell<T extends DataType>({
  children,
  column,
  sortIcons,
  enableSortingIcons = true,
  onSort,
  headerProps,
  ...rest
}: TableHeadCellProps<T>) {
  const defaultIcons = {
    ascending: sortIcons?.ascending ?? <FaSortUp />,
    descending: sortIcons?.descending ?? <FaSortDown />,
    unsorted: sortIcons?.unsorted ?? <FaSort color="#ccc" />,
  };

  let sortIcon: React.ReactNode = null;

  if (enableSortingIcons && column.canSort) {
    if (column.isSorted) {
      sortIcon = column.isSortedDesc ? defaultIcons.descending : defaultIcons.ascending;
    } else {
      sortIcon = defaultIcons.unsorted;
    }
  }

  const handleClick = () => {
    if (!column.canSort) return;
    const desc = column.isSorted ? !column.isSortedDesc : false;
    onSort?.(column.id, desc);
    const mockEvent = { persist: () => {} };
    column.getSortByToggleProps?.().onClick?.(mockEvent as React.MouseEvent);
  };

  return (
    <Box
      {...column.getHeaderProps(column.getSortByToggleProps({ title: undefined }))}
      onClick={handleClick}
      display="flex"
      alignItems="center"
      justifyContent={headerProps?.justifyContent ?? 'flex-start'}
      px={headerProps?.px ?? 2}
      py={headerProps?.py ?? 1}
      cursor={column.canSort ? 'pointer' : 'default'}
      fontWeight={headerProps?.fontWeight ?? 'semibold'}
      fontSize={headerProps?.fontSize ?? 'sm'}
      color={column.isSorted ? 'blue.600' : headerProps?.color ?? 'gray.700'}
      _hover={column.canSort ? { bg: 'gray.50' } : undefined}
      {...rest}
    >
      {children}
      {enableSortingIcons && sortIcon && (
        <Box ml={2} display="flex">
          <Box
            as="span"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            p={1}
            color={column.isSorted ? 'blue.600' : 'gray.400'}
            borderRadius="md"
            transition="background-color 0.2s, color 0.2s"
          >
            {sortIcon}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export interface TableHeadProps<T extends DataType> extends BoxProps {
  headerGroups: HeaderGroup<T>[];
  sortBy?: SortingRule<T>[];
  sortIcons?: SortIcons;
  enableSortingIcons?: boolean;
  sticky?: boolean;
  headerBg?: string;
  borderColor?: string;
  headerProps?: ChakraProps;
  onSort?: (columnId: string, desc: boolean | undefined) => void;
}

function TableHeadComponent<T extends DataType>({
  headerGroups,
  sortBy,
  sortIcons,
  enableSortingIcons = true,
  sticky = true,
  headerBg,
  borderColor,
  headerProps,
  onSort,
  ...rest
}: TableHeadProps<T>) {
  const tableColor = colors.table;

  return (
    <Box
      sx={{
        borderBottom: '1px solid #e5e5e5',
        backgroundColor: tableColor.headerBg,
      }}
      position={sticky ? 'sticky' : 'relative'}
      top={0}
      zIndex={1}
      {...rest}
    >
      {headerGroups.map((headerGroup) => (
        <Box {...headerGroup.getHeaderGroupProps()} key={headerGroup.id} display="flex" flexDirection="row">
          {headerGroup.headers.map((column) => (
            <TableHeadCell<T>
              key={column.id}
              column={column}
              sortIcons={sortIcons}
              enableSortingIcons={enableSortingIcons}
              headerProps={headerProps}
              onSort={onSort}
            >
              {column.render('Header')}
            </TableHeadCell>
          ))}
        </Box>
      ))}
    </Box>
  );
}

export const TableHead = React.memo(TableHeadComponent) as <T extends Record<string, unknown>>(
  props: TableHeadProps<T>,
) => JSX.Element;
